import React from 'react';
import { Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ScoreEntry, Verdict } from '../../../utils/scoring';

interface VerdictButtonsProps {
  /** Points the verdict is worth; null hides the buttons (nothing to judge). */
  points: number | null;
  /** Verdict already given on this question — shown as a badge instead of buttons. */
  verdict?: ScoreEntry | null;
  onJudge: (verdict: Verdict) => void;
  className?: string;
}

export const formatPoints = (points: number) => (points > 0 ? `+${points}` : `${points}`.replace('-', '−'));

/** Correct / wrong buttons for one answering player, styled for the dark buzzer stage. */
const VerdictButtons: React.FC<VerdictButtonsProps> = ({ points, verdict, onJudge, className }) => {
  if (verdict) {
    return (
      <span
        className={cn(
          'shrink-0 rounded-full px-2.5 py-1 text-sm font-black tabular-nums',
          verdict.verdict === 'correct' ? 'bg-emerald-400/15 text-emerald-300' : 'bg-rose-500/15 text-rose-300',
          className
        )}
      >
        {formatPoints(verdict.points)}
      </span>
    );
  }

  if (points === null) return null;

  return (
    <div className={cn('flex shrink-0 items-center gap-2', className)}>
      <button
        type="button"
        onClick={() => onJudge('correct')}
        title="Düz cavab"
        aria-label={`Düz cavab, +${points}`}
        style={{ borderRadius: '0.625rem' }}
        className="inline-flex h-10 items-center gap-1 bg-emerald-500 px-3 text-sm font-black text-white transition hover:bg-emerald-600"
      >
        <Check className="h-4 w-4" />
        <span className="tabular-nums">+{points}</span>
      </button>
      <button
        type="button"
        onClick={() => onJudge('wrong')}
        title="Səhv cavab"
        aria-label={`Səhv cavab, −${points}`}
        style={{ borderRadius: '0.625rem' }}
        className="inline-flex h-10 items-center gap-1 bg-rose-600 px-3 text-sm font-black text-white transition hover:bg-rose-700"
      >
        <X className="h-4 w-4" />
        <span className="tabular-nums">−{points}</span>
      </button>
    </div>
  );
};

export default VerdictButtons;
